<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  CARD_TEMPLATE_ICONS,
  fieldsForTemplateSurface,
} from 'dashboard/helper/kanbanCardTemplate';

const props = defineProps({
  item: { type: Object, required: true },
  presentation: { type: Object, default: null },
  surface: { type: String, default: 'face' },
  preview: { type: Boolean, default: false },
});

const { t } = useI18n();
const priorityLabels = computed(() => ({
  HIGH: t('KANBAN.PRIORITY.HIGH'),
  MEDIUM: t('KANBAN.PRIORITY.MEDIUM'),
  LOW: t('KANBAN.PRIORITY.LOW'),
  NONE: t('KANBAN.PRIORITY.NONE'),
}));

const activePresentation = computed(
  () => props.presentation || props.item?.kanban_card_template?.presentation
);
const fields = computed(() =>
  fieldsForTemplateSurface({
    item: props.item,
    presentation: activePresentation.value,
    surface: props.surface,
    preview: props.preview,
  })
);
const densityClass = computed(() => {
  const density = activePresentation.value?.density;
  if (density === 'compact') return 'gap-1.5';
  if (density === 'roomy') return 'gap-4';
  return 'gap-2.5';
});
const groups = computed(() => {
  if (props.surface !== 'detail') return [{ name: null, fields: fields.value }];
  return fields.value.reduce((result, field) => {
    const name = field.group || 'Informações';
    let group = result.find(entry => entry.name === name);
    if (!group) {
      group = { name, fields: [] };
      result.push(group);
    }
    group.fields.push(field);
    return result;
  }, []);
});

const fieldClass = field => [
  field.width === 'half' ? 'col-span-1' : 'col-span-2',
  field.size === 'large' ? 'text-base' : '',
  field.size === 'small' ? 'text-xs' : 'text-sm',
];

const fieldDisplayValue = field => {
  if (
    field.source === 'item_details' &&
    field.key === 'priority' &&
    !field.empty
  ) {
    const priority = String(field.displayValue).toUpperCase();
    if (priorityLabels.value[priority]) return priorityLabels.value[priority];
  }
  return field.displayValue;
};
</script>

<template>
  <div
    data-testid="kanban-card-template-renderer"
    class="flex flex-col"
    :class="densityClass"
  >
    <section v-for="group in groups" :key="group.name || 'face'">
      <h4
        v-if="group.name"
        class="mb-2 text-xs font-semibold uppercase tracking-wide text-n-slate-10"
      >
        {{ group.name }}
      </h4>
      <div class="grid grid-cols-2 gap-2">
        <div
          v-for="field in group.fields"
          :key="`${field.source}:${field.key}`"
          class="min-w-0 rounded-lg border border-n-weak bg-n-solid-2 px-2.5 py-2"
          :class="[
            fieldClass(field),
            { 'border-dashed opacity-60': field.empty },
          ]"
          :data-field-key="`${field.source}:${field.key}`"
        >
          <div class="flex items-center gap-1.5 text-n-slate-10">
            <span
              class="size-3.5 shrink-0"
              :class="[CARD_TEMPLATE_ICONS[field.icon]]"
            />
            <span
              class="truncate text-[10px] font-medium uppercase tracking-wide"
            >
              {{ field.label }}
            </span>
          </div>
          <p class="mt-1 break-words font-medium text-n-slate-12">
            {{ fieldDisplayValue(field) }}
          </p>
        </div>
      </div>
    </section>
  </div>
</template>
